import React, { useState, useEffect } from "react";
import {
  Box,
  Text,
  TextInput,
  Select,
  Button,
  Heading,
  Notification,
  Layer,
} from "grommet";
import { Info } from "grommet-icons";
import adminAxios from "../../config/adminAxios";
import { useNavigate } from "react-router-dom";

const AddCourseModal = ({
  onClose,
  portfolio_id,
  isAddQuestionMode = false,
  selected_skill_id,
}) => {
  // Dropdown options
  const courseModes = [
    { label: "ILT/VILT", value: "ILT/VILT" },
    { label: "eLearning", value: "eLearning" },
  ];
  const oemOptions = [
    { label: "Select Vendor", value: "", disabled: true },
    { label: "HPE", value: "HPE" },
    { label: "SUSE", value: "SUSE" },
    { label: "Red Hat", value: "Red Hat" },
    { label: "PRINCE", value: "PRINCE" },
    { label: "OpsRamp", value: "OpsRamp" },
    { label: "NVidia", value: "NVidia" },
    { label: "NIST", value: "NIST" },
    { label: "Morpheus", value: "morpheus" },
    { label: "Microsoft", value: "Microsoft" },
    { label: "Meteoros", value: "Met" },
    { label: "ITIL", value: "ITIL" },
    { label: "CompTIA", value: "CompTIA" },
    { label: "Other", value: "Other" },
  ];
  const courseLevels = [
    { label: "Select Level", value: "", disabled: true },
    { label: "Prerequisite", value: "0" },
    { label: "Foundational", value: "1" },
    { label: "Intermediate", value: "2" },
  ];

  // Form state
  const [courseName, setCourseName] = useState("");
  const [courseCode, setCourseCode] = useState("");
  const [courseMode, setCourseMode] = useState([]);
  const [selectedSkill, setSelectedSkill] = useState("");
  const [skills, setSkills] = useState([]);
  const [oem, setOem] = useState("");
  const [courseLevel, setCourseLevel] = useState("");
  const [courseDuration, setCourseDuration] = useState("");
  const [durationUnit, setDurationUnit] = useState("Hours");
  const [showNotification, setShowNotification] = useState(false);
  const [isButtonClicked, setIsButtonClicked] = useState(false);

  // Datasheet URLs
  const [datasheetUrls, setDatasheetUrls] = useState([]);

  // Learning Path Modal state (shown only in Add Question mode)
  const [showLearningPathModal, setShowLearningPathModal] = useState(false);
  const [learningPaths, setLearningPaths] = useState([]);
  // const [selectedLearningPath, setSelectedLearningPath] = useState("");
  const [createdCourseIds, setCreatedCourseIds] = useState([]); // store created course ids

  // Base course code without suffixes
  const baseCode = courseCode.replace(/S$|AAE$/, "");
  const navigate = useNavigate();

  // Validation
  const isFormValid =
    courseName.trim() &&
    courseCode.trim() &&
    courseMode.length > 0 &&
    selectedSkill &&
    oem &&
    courseLevel &&
    courseDuration.trim();

  // Fetch skills for portfolio
  useEffect(() => {
    if (!portfolio_id) return;
    adminAxios
      .get(`/api/admin/get-skills/${portfolio_id}`)
      .then((res) => setSkills(res.data.skills || []))
      .catch((err) => console.error("Error fetching skills:", err));
  }, [portfolio_id]);

  useEffect(() => {
    const skillObj = skills.find(
      (s) => Number(s.skillId) === Number(selected_skill_id),
    );
    setSelectedSkill(skillObj);
  }, [selected_skill_id, skills]);

  // Auto-append suffix to courseCode based on mode (if single mode selected)
  useEffect(() => {
    if (!Array.isArray(courseMode)) return;
    if (courseMode.length === 1) {
      const suffix = courseMode[0].value === "ILT/VILT" ? "S" : "AAE";
      setCourseCode(baseCode + suffix);
    } else if (courseMode.length === 0) {
      setCourseCode(baseCode);
    }
  }, [courseMode, baseCode]);

  // Generate datasheet URLs
  useEffect(() => {
    if (!baseCode) {
      setDatasheetUrls([]);
      return;
    }
    const prefix = `https://education.hpe.com/ww/en/training/docs/cds/${baseCode}`;
    if (courseMode.length === 2) {
      setDatasheetUrls([`${prefix}S.pdf`, `${prefix}AAE.pdf`]);
    } else if (courseMode[0] && courseMode[0].value === "ILT/VILT") {
      setDatasheetUrls([`${prefix}S.pdf`]);
    } else if (courseMode[0] && courseMode[0].value === "eLearning") {
      setDatasheetUrls([`${prefix}AAE.pdf`]);
    } else {
      setDatasheetUrls([]);
    }
  }, [baseCode, courseMode]);

  // Create course(s) API call
  const createCourse = async () => {
    setIsButtonClicked(true);

    try {
      let createdIds = [];

      if (courseMode.length === 2) {
        // Two courses payload
        const payloadILT = {
          courseName,
          courseCode: baseCode + "S",
          courseMode: "ILT/VILT",
          skillId: selectedSkill.skillId || selectedSkill,
          oem,
          courseLevel,
          courseDuration,
          durationUnit,
        };
        const payloadEle = {
          courseName,
          courseCode: baseCode + "AAE",
          courseMode: "eLearning",
          skillId: selectedSkill.skillId,
          oem,
          courseLevel,
          courseDuration,
          durationUnit,
        };
        // Assuming backend returns created courses array with ids
        const response = await adminAxios.post("/api/admin/add-course", {
          courses: [payloadILT, payloadEle],
        });
        createdIds = response.data.createdCourseIds || [];
      } else {
        const payload = {
          courseName,
          courseCode,
          courseMode,
          skillId: selectedSkill.skillId,
          oem,
          courseLevel,
          courseDuration,
          durationUnit,
        };
        const response = await adminAxios.post("/api/admin/add-course", {
          courses: [payload],
        });
        createdIds = response.data.createdCourseIds || []; // single course id
      }

      setCreatedCourseIds(createdIds);

      if (isAddQuestionMode) {
        // Open learning path modal after course creation
        setShowLearningPathModal(true);
      } else {
        // Show success notification and close
        setShowNotification(true);
        setTimeout(() => {
          setShowNotification(false);
          onClose();
          window.location.reload();
        }, 1500);
      }
    } catch (err) {
      console.error("Error adding course:", err);
      setIsButtonClicked(false);
    }
  };

  // Handle "Add Learning Path" button click from modal
  const handleAddLearningPath = async () => {
    // if (!createdCourseIds.length) return;
    // console.log("Redirecting");
    navigate(`/admin/portfolio/${portfolio_id}/learning-paths`);
  };

  // Fetch learning paths for the portfolio when modal opens
  useEffect(() => {
    if (showLearningPathModal) {
      adminAxios
        .get(`/api/admin/learning-paths-for-skill/${selected_skill_id}`)
        .then((res) => {
          setLearningPaths(res.data.learningPaths || []);
        })
        .catch((err) => console.error("Error fetching learning paths:", err));
    }
  }, [showLearningPathModal, portfolio_id]);

  const handleCreateCourseClick = () => {
    if (!isFormValid) return;
    createCourse();
  };

  return (
    <Box pad="medium" gap="small" width="large">
      {showNotification && (
        <Notification
          toast
          status="normal"
          message="Course created successfully"
          onClose={() => setShowNotification(false)}
        />
      )}

      <Heading level={2} margin="none">
        Add New Course
      </Heading>

      <Text size="small" color="status-warning">
        Please proceed filling the details in order.
      </Text>

      {/* Course Name */}
      <Box>
        <Text>
          Course Name <Text color="red">*</Text>
        </Text>
        <TextInput
          placeholder="Enter the course name"
          value={courseName}
          onChange={(e) => setCourseName(e.target.value)}
        />
      </Box>

      {/* Course Code */}
      <Box>
        <Text>
          Course Code <Text color="red">*</Text>
        </Text>
        <TextInput
          placeholder="Enter the course code"
          value={courseCode}
          onChange={(e) => setCourseCode(e.target.value)}
        />
        <Box direction="row" margin={{ top: "xsmall" }}>
          <Info color="brand" />
          <Text size="small" margin={{ left: "xsmall" }}>
            Enter code without trailing “S” or “AAE”. Suffix will be added based
            on mode.
          </Text>
        </Box>
      </Box>

      {/* Mode & Skill */}
      <Box direction="row" gap="small">
        <Box flex>
          <Text>
            Course Mode<Text color="red">*</Text>
          </Text>
          <Select
            multiple
            placeholder="Select course mode (multiple)"
            options={courseModes}
            labelKey="label"
            valueKey="value"
            value={courseMode}
            onChange={({ value }) => setCourseMode(value)}
          />
        </Box>
        <Box flex>
          <Text>
            Select Skill <Text color="red">*</Text>
          </Text>
          {isAddQuestionMode ? (
            // Find the skill name by selectedSkill id

            <Text>
              {skills.find(
                (s) => Number(s.skillId) === Number(selected_skill_id),
              )?.skillName || "N/A"}
            </Text>
          ) : (
            <Select
              placeholder="Select skill"
              options={skills}
              labelKey="skillName"
              valueKey="skillId"
              value={selectedSkill}
              onChange={({ value }) => setSelectedSkill(value)}
              dropHeight="185px"
            />
          )}
        </Box>
      </Box>

      {/* OEM & Level */}
      <Box direction="row" gap="small">
        <Box flex>
          <Text>
            Vendor <Text color="red">*</Text>
          </Text>
          <Select
            options={oemOptions}
            labelKey="label"
            valueKey="value"
            value={oem}
            onChange={({ option }) => !option.disabled && setOem(option.value)}
            dropHeight="185px"
          />
        </Box>
        <Box flex>
          <Text>
            Course Level <Text color="red">*</Text>
          </Text>
          <Select
            options={courseLevels}
            labelKey="label"
            valueKey="value"
            value={courseLevel}
            onChange={({ option }) =>
              !option.disabled && setCourseLevel(option.value)
            }
          />
        </Box>
      </Box>

      {/* Duration */}
      <Box>
        <Text>
          Course Duration <Text color="red">*</Text>
        </Text>
        <Box direction="row" gap="small">
          <Box flex>
            <TextInput
              type="number"
              min="0"
              placeholder="Number"
              value={courseDuration}
              onChange={(e) => setCourseDuration(e.target.value)}
            />
          </Box>
          <Box flex>
            <Select
              options={
                courseMode.length === 1 && courseMode[0].value === "ILT/VILT"
                  ? ["Days"]
                  : ["Hours", "Days"]
              }
              value={durationUnit}
              onChange={({ option }) => setDurationUnit(option)}
              disabled={!courseMode[0]}
            />
          </Box>
        </Box>
      </Box>

      {/* Datasheet URL(s) */}
      <Box>
        <Text>Datasheet URL{datasheetUrls.length > 1 ? "s" : ""}</Text>
        {datasheetUrls.map((url) => (
          <Box key={url} direction="row" gap="small" margin={{ top: "small" }}>
            <TextInput readOnly flex value={url} />
            <Button
              label="Open"
              onClick={() => window.open(url, "_blank")}
              disabled={!url}
            />
          </Box>
        ))}
      </Box>

      {/* Actions */}
      <Box direction="row" justify="end" gap="small">
        <Button label="Cancel" onClick={onClose} />
        <Button
          label="Create Course"
          primary
          onClick={handleCreateCourseClick}
          disabled={!isFormValid || isButtonClicked}
        />
      </Box>

      {/* Learning Path Selection Modal */}
      {showLearningPathModal && (
        <Layer
          onEsc={() => setShowLearningPathModal(false)}
          onClickOutside={() => setShowLearningPathModal(false)}
        >
          <Box
            pad="medium"
            gap="small"
            width="medium"
            background="white"
            round="small"
          >
            <Heading level={3} margin="none">
              Add Course to Learning Path
            </Heading>
            <Text
              size="medium"
              color="status-warning"
              margin={{ bottom: "small" }}
            >
              Before you can add a newly created course to a question, it is
              essential to associate the course with an existing learning path.
              Please match the course to a learning path to proceed with adding
              the course to the question.
            </Text>

            <Box margin={{ bottom: "small" }}>
              <Text weight="bold" margin={{ bottom: "small" }}>
                Existing Learning Paths for this Skill:
              </Text>
              {learningPaths && learningPaths.length > 0 ? (
                <Box pad={{ left: "small" }} gap="xsmall">
                  {learningPaths.map((lp) => (
                    <Text key={lp.learningpath_id}>
                      • {lp.learningpath_name}
                    </Text>
                  ))}
                </Box>
              ) : (
                <Text size="small" color="status-disabled">
                  No learning paths found.
                </Text>
              )}
            </Box>

            <Box
              direction="row"
              gap="small"
              justify="end"
              margin={{ top: "small" }}
            >
              <Button
                label="Ok"
                onClick={() => {
                  setShowLearningPathModal(false);
                  setShowNotification(true);
                  setTimeout(() => {
                    setShowNotification(false);
                    onClose();
                    window.location.reload();
                  }, 1500);
                }}
              />
              <Button
                label="Add Learning Path"
                primary
                onClick={handleAddLearningPath}
              />
            </Box>
          </Box>
        </Layer>
      )}
    </Box>
  );
};

export default AddCourseModal;
